export interface ApiKeyItem {
  id: string;
  name: string;
  keyPrefix: string;
  scopes: string[];
  isActive: boolean;
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface ApiKeyCreated extends ApiKeyItem {
  plaintextKey: string;
}

export interface SyncLogEntry {
  id: string;
  tenantId: string;
  apiKeyId: string | null;
  apiKeyName: string | null;
  method: string;
  endpoint: string;
  statusCode: number;
  recordCount: number | null;
  errorMessage: string | null;
  durationMs: number | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Die Scopes, die ein Administrator vergeben kann — **vollständig**.
 *
 * Vollständig ist hier eine Zusicherung und keine Beobachtung: Bis zum
 * 12.9.2026 fehlte `time_entries:write`, und damit ließ sich über die
 * Oberfläche kein Schlüssel anlegen, der eine Übernahme bestätigen darf
 * (`acknowledge`) — also kein vollständiger Rücksync-Kreis. Der Scope existierte
 * im Backend, die Liste hier kannte ihn nur nicht. *Eine Berechtigung, die es
 * gibt und die niemand vergeben kann, ist schlimmer als eine, die es nicht
 * gibt: Sie steht in der Dokumentation.*
 *
 * `api-schluessel-scopes.test.ts` hält die Liste gegen das Backend.
 */
export const SCOPE_LABELS: Record<string, string> = {
  'time_entries:read': 'Zeitbuchungen lesen',
  'time_entries:write': 'Übernahme bestätigen (Buchungen und Löschungen als synchronisiert melden)',
  'payroll:read': 'Lohnverrechnung lesen',
  'users:read': 'Mitarbeiter lesen',
  // AP-35 Phase K: Positionen und Arbeitsschritte nur mit dem Baustein
  // Projektmanagement. „Projekte" bleibt fest: Die Konstante liegt außerhalb
  // jeder Komponente, und die Nachbarzeile benennt dieselben Ressourcen ebenso.
  'selection_data:read': 'Auswahldaten lesen (Projekte, Positionen, Arbeitsschritte, Tätigkeiten)',
  'time_master_data:write': 'Stammdaten synchronisieren (Projekte, Positionen, Schritte, Tätigkeiten, Mitarbeiter)',
  'webhooks:manage': 'Webhooks verwalten',
  'prices:read': 'Stundensätze und Beträge lesen',
  'articles:read': 'Artikel lesen',
  'articles:write': 'Artikel synchronisieren (anlegen und ändern über die Fremd-ID)',
  'purchase:read': 'Einkaufsdaten lesen (Lieferanten, Einkaufspreise)',
  'site_docs:read': 'Baustellendokumente abholen (freigegebene Dokumente mit Inhalt und PDF)',
};

/**
 * AP-30-G — Die Scopes, die **Felder** freigeben statt Endpunkte.
 *
 * Sie gehören in der Maske getrennt, weil sie eine andere Frage beantworten:
 * nicht „wohin darf dieser Zugang", sondern „was steht in der Antwort". Ein
 * Schlüssel mit `prices:read` und ohne `time_entries:read` kommt nirgends hin.
 */
export const FIELD_SCOPES = ['prices:read', 'purchase:read'] as const;

export const ALL_SCOPES = Object.keys(SCOPE_LABELS);

/** Die Endpunkt-Scopes — alles, was kein Feld-Scope ist. */
export const ENDPOINT_SCOPES = ALL_SCOPES.filter(
  (s) => !(FIELD_SCOPES as readonly string[]).includes(s),
);
